import { TimeRangeUtil } from '../../app/utils/time';
import { ChartMode } from '../context/investigate-chart-context';

import {
  ViewState,
  ViewType,
  TabbedView,
  AlertBoardView,
  AlertBoardPreviewState,
  AlertManagerView,
  LimitManagerView,
  ManageMonitorView,
  ManageFormulasView,
  ManageInheritanceView,
  ManageAlertsView,
  ManageUserGroupsView,
  ManageWebhooksView,
  PersistView,
  PersistTimeSelection,
  ViewShareType,
  Ensemble,
  ParentTag,
  HiddenTag,
  Tag,
  TrendSearchData,
  AnomalySort,
  AssessMode,
  DataModeType,
  View,
} from '@controlrooms/models';

/**
 * Chart height increments for assess mode
 */
export const chartHeightIncrements = [45, 52, 72, 92, 112, 132, 152, 172, 202];

/**
 * Get the current view mode data from ViewState based on tabType and selectedMode/subPage
 */
export const getCurrentViewData = (viewState: ViewState) => {
  if (viewState.tabType === 'VIEWS') {
    return viewState.view[viewState.selectedMode];
  }
  return undefined;
};

/**
 * Get AlertBoardView from ViewState (for ALERT_BOARD tabs)
 */
export const getAlertBoardView = (viewState: ViewState): AlertBoardView | undefined => {
  if (viewState.tabType === 'ALERT_BOARD') {
    return viewState.view[ViewType.ALERT_BOARD];
  }
  return undefined;
};

/**
 * Get AlertManagerView from ViewState (for ALERT_MANAGER tabs)
 */
export const getAlertManagerView = (viewState: ViewState): AlertManagerView | undefined => {
  if (viewState.tabType === 'ALERT_MANAGER') {
    return viewState.view[ViewType.ALERT_MANAGER];
  }
  return undefined;
};

/**
 * Get LimitManagerView from ViewState (for LIMIT_MANAGER tabs)
 */
export const getLimitManagerView = (viewState: ViewState): LimitManagerView | undefined => {
  if (viewState.tabType === 'LIMIT_MANAGER') {
    return viewState.view[ViewType.LIMIT_MANAGER];
  }
  return undefined;
};

/**
 * Get ManageView from ViewState based on subPage (for MANAGE tabs)
 */
export const getManageView = (
  viewState: ViewState,
):
  | ManageMonitorView
  | ManageFormulasView
  | ManageInheritanceView
  | ManageAlertsView
  | ManageUserGroupsView
  | ManageWebhooksView
  | undefined => {
  if (viewState.tabType === 'MANAGE') {
    switch (viewState.subPage) {
      case 'monitors':
        return viewState.view[ViewType.MANAGE_MONITOR];
      case 'formulas':
        return viewState.view[ViewType.MANAGE_FORMULAS];
      case 'inheritance':
        return viewState.view[ViewType.MANAGE_INHERITANCE];
      case 'alerts':
        return viewState.view[ViewType.MANAGE_ALERTS];
      case 'user_groups':
        return viewState.view[ViewType.MANAGE_USER_GROUPS];
      case 'webhooks':
        return viewState.view[ViewType.MANAGE_WEBHOOKS];
      default:
        return viewState.view[ViewType.MANAGE_MONITOR];
    }
  }
  return undefined;
};

/**
 * Helper to access view[ViewType] for VIEWS tabs
 */
export const getViewByType = (view: TabbedView, viewType: ViewType) => {
  return view[viewType];
};

/**
 * Transform PersistView from backend to ViewState
 */
export const transformToViewState = (
  view: PersistView,
  ensembleFamily: Ensemble | null,
  tenant: number,
  overrideViewId = '',
): ViewState => {
  return {
    userId: view.user_id,
    created_by: view.created_by,
    version: view.version || 1,
    viewId: view.default_view ? 'default' : view.view_id || overrideViewId,
    name: view.view.name || '',
    description: view.view.description || '',
    shared: !!view.shared,
    shareType:
      view.share_type && view.share_type !== ('' as ViewShareType)
        ? view.share_type
        : ViewShareType.PRIVATE,
    isDefault: !!view.default_view,
    selectedEnsemble: ensembleFamily,
    view_id: view.view_id || overrideViewId || '',
    selectedMode: view.view.type || ViewType.MONITOR,
    tabType: view.view_type || 'VIEWS', // Default to VIEWS for backward compatibility
    subPage: view.sub_page,
    isHidden: false,
    isDirty: false,
    isDeleted: false,
    tenant_id: tenant,
    timeSelection: PersistTimeSelection.toTimeSelection(
      view.view.timeSelection.streamingTimeInSeconds
        ? {
            ...view.view.timeSelection,
            ...TimeRangeUtil.ofDurationV2(view.view.timeSelection.streamingTimeInSeconds),
          }
        : { ...view.view.timeSelection },
    ),
    dataModeType: view.view.dataModeType || DataModeType.AUTO,
    view: (() => {
      const tabbedView: Partial<TabbedView> = {
        [ViewType.MONITOR]: {
          severityFilter: view.view.type === 'monitor' ? view.view.severityFilter || 1 : 1,
          showLimits: view.view.showLimits ?? view.view.showMonitorLimits ?? true,
          selectedFolders: view.view.selectedFolders || [],
          selectedTags:
            (view.view.type === 'monitor' ? (view.view.selectedTags as string[]) : []) || [],
        },
        [ViewType.ANALYZE]: {
          showLimits: view.view.showLimits ?? view.view.showAnalyzeLimits ?? true,
          showLabeledEvents: view.view.showLabeledEvents ?? true,
          pinnedTags:
            (view.view.type === 'analyze' ? view.view.pinnedTags : []) || ([] as ParentTag[]),
          hiddenTags:
            (view.view.type === 'analyze' ? view.view.hiddenTags : []) || ([] as HiddenTag[]),
          selectedFolders: view.view.selectedFolders || [],
          selectedTags:
            (view.view.type === 'analyze' ? (view.view.selectedTags as string[]) : []) || [],
          trendSearchData:
            (view.view.type === 'analyze' ? view.view.trendSearchData : undefined) ||
            ({} as TrendSearchData),
          isTrendSearch: view.view.type === 'analyze' ? view.view.isTrendSearch : false,
        },
        [ViewType.INVESTIGATE]: {
          selectedFolders: view.view.selectedFolders || [],
          selectedTags:
            (view.view.type === 'investigate'
              ? (view.view.selectedTags as { [folder: number]: Tag[] })
              : {}) || {},
          showAnomalies: view.view.showAnomalies ?? true,
          organize:
            (view.view.type === 'investigate' ? view.view.chartMode : 'overlays') ||
            ('overlays' as ChartMode),
          groupByUom: view.view.groupByUom ?? false,
          showLimits: view.view.showLimits ?? view.view.showInvestigateLimits ?? true,
        },
      };

      const tabType = view.view_type || 'VIEWS';
      const subPage = view.sub_page;
      // view.view is typed as View (union type), but we need to access properties that may not exist on all types
      // Convert through unknown first for safe property access
      const viewData = view.view as unknown as Record<string, unknown>;

      // Initialize defaults for other view types based on tabType
      let alertBoardView: AlertBoardView = {};
      let alertManagerView: AlertManagerView = {};
      let limitManagerView: LimitManagerView = {};
      let manageMonitorView: ManageMonitorView = {};
      let manageFormulasView: ManageFormulasView = {};
      let manageInheritanceView: ManageInheritanceView = {};
      let manageAlertsView: ManageAlertsView = {};
      let manageUserGroupsView: ManageUserGroupsView = {};
      let manageWebhooksView: ManageWebhooksView = {};

      // If the view is for a specific tab type, populate from view data
      if (tabType === 'ALERT_BOARD') {
        const previewData = viewData?.preview as AlertBoardPreviewState | undefined;
        const persistTimeSel =
          (viewData?.time_selection as PersistTimeSelection | undefined) || view.view.timeSelection;
        const rawBoardTimeSel = viewData?.time_selection as PersistTimeSelection | undefined;
        const derivedPreviewTimeSelection =
          rawBoardTimeSel &&
          typeof rawBoardTimeSel === 'object' &&
          'startTime' in rawBoardTimeSel &&
          typeof rawBoardTimeSel.startTime === 'string'
            ? PersistTimeSelection.toTimeSelection(rawBoardTimeSel)
            : null;
        alertBoardView = {
          selected_board: (viewData?.selected_board as string | undefined) || '',
          selected_view: (viewData?.selected_view as string | undefined) || 'grid',
          time_selection: persistTimeSel,
          filters: (viewData?.filters as Record<string, unknown> | undefined) || {},
          preview: {
            timeSelection: previewData?.timeSelection ?? derivedPreviewTimeSelection ?? null,
            timeSelectionHistory: previewData?.timeSelectionHistory ?? [],
          },
        };
      }
      if (tabType === 'ALERT_MANAGER') {
        alertManagerView = {
          folder: viewData?.folder as number | undefined,
          tag: (viewData?.tag as string | undefined) || '',
          rule_id: (viewData?.rule_id as string | undefined) || '',
        };
      }
      if (tabType === 'LIMIT_MANAGER') {
        limitManagerView = {
          folder: viewData?.folder as number | undefined,
          tag: (viewData?.tag as string | undefined) || '',
          limit_id: (viewData?.limit_id as string | undefined) || '',
        };
      }
      if (tabType === 'MANAGE') {
        const manageData = {
          folder: viewData?.folder as number | undefined,
          tag: (viewData?.tag as string | undefined) || '',
          ensemble_id: (viewData?.ensemble_id as string | undefined) || '',
          user_group_id: viewData?.user_group_id as number | undefined,
          webhook_id: (viewData?.webhook_id as string | undefined) || '',
        };
        switch (subPage) {
          case 'monitors':
            manageMonitorView = {
              folder: manageData.folder,
              tag: manageData.tag,
              ensemble_id: manageData.ensemble_id,
              tabPath: viewData?.tabPath as string[] | undefined,
            };
            break;
          case 'formulas':
            manageFormulasView = {
              folder: manageData.folder,
              tag: manageData.tag,
              ensemble_id: manageData.ensemble_id,
            };
            break;
          case 'inheritance':
            manageInheritanceView = {
              folder: manageData.folder,
              tag: manageData.tag,
              ensemble_id: manageData.ensemble_id,
            };
            break;
          case 'alerts':
            manageAlertsView = {
              folder: manageData.folder,
              tag: manageData.tag,
            };
            break;
          case 'user_groups':
            manageUserGroupsView = {
              user_group_id: manageData.user_group_id,
            };
            break;
          case 'webhooks':
            manageWebhooksView = {
              webhook_id: manageData.webhook_id,
            };
            break;
          default:
            // Default to monitors if subPage is not recognized
            manageMonitorView = {
              folder: manageData.folder,
              tag: manageData.tag,
              ensemble_id: manageData.ensemble_id,
              tabPath: viewData?.tabPath as string[] | undefined,
            };
        }
      }

      return {
        // VIEWS tab types - direct indexing
        ...tabbedView,
        // Initialize defaults for other view types
        [ViewType.ALERT_BOARD]: alertBoardView,
        [ViewType.ALERT_MANAGER]: alertManagerView,
        [ViewType.LIMIT_MANAGER]: limitManagerView,
        [ViewType.MANAGE_MONITOR]: manageMonitorView,
        [ViewType.MANAGE_FORMULAS]: manageFormulasView,
        [ViewType.MANAGE_INHERITANCE]: manageInheritanceView,
        [ViewType.MANAGE_ALERTS]: manageAlertsView,
        [ViewType.MANAGE_USER_GROUPS]: manageUserGroupsView,
        [ViewType.MANAGE_WEBHOOKS]: manageWebhooksView,
      } as TabbedView;
    })(),
    config: {
      [ViewType.ANALYZE]: {
        chartHeight: view.view.config?.[ViewType.ANALYZE]?.chartHeight || chartHeightIncrements[0],
        limitFilters: view.view.config?.[ViewType.ANALYZE]?.limitFilters || {
          HH: true,
          H: true,
          L: true,
          LL: true,
        },
        sortOption: view.view.config?.[ViewType.ANALYZE]?.sortOption || AnomalySort.SEVERITY,
        assessMode: view.view.config?.[ViewType.ANALYZE]?.assessMode || AssessMode.MODEL,
        modelFilterTypes: view.view.config?.[ViewType.ANALYZE]?.modelFilterTypes || [
          'UNIVARIATE',
          'MULTIVARIATE',
        ],
        showOnlyAlertedModels: view.view.config?.[ViewType.ANALYZE]?.showOnlyAlertedModels || false,
        selectedEnsembleId: view.view.config?.[ViewType.ANALYZE]?.selectedEnsembleId || null,
        browserState: {
          expandedSystems:
            view.view.config?.[ViewType.ANALYZE]?.browserState?.expandedSystems || [],
        },
      },
    },
    timeSelectionHistory: [],
  };
};

/**
 * Transform ViewState to PersistView for backend.
 * Tab content is determined solely by: tabType, selectedMode (for VIEWS), and subPage (for MANAGE).
 * These three drive which view slice is read and what is written to PersistView (view.type, view_type, sub_page).
 */
export const constructPersistedView = (viewObj: {
  viewState: ViewState;
  viewName: string;
  viewDescription: string;
  shareType: ViewShareType;
  currentUserId: number;
  shared: boolean;
}): PersistView => {
  const { viewState, viewName, viewDescription, shareType, currentUserId, shared } = viewObj;
  const persistTimeSelection = PersistTimeSelection.ofTimeSelection(viewState.timeSelection);

  // Content drivers: tab content is decided by tabType, selectedMode, and subPage only
  const tabType = viewState.tabType || 'VIEWS';
  const selectedMode = viewState.selectedMode ?? ViewType.MONITOR;
  const subPage = viewState.subPage;

  // Base view data structure that will be extended based on tabType / selectedMode / subPage
  // Use View & Record<string, unknown> to allow additional properties for non-VIEWS tab types
  let viewData: Partial<View> & Record<string, unknown> = {
    name: viewName,
    description: viewDescription,
    timeSelection: persistTimeSelection,
    ensemble_family_id: viewState?.selectedEnsemble?.family_id,
    dataModeType: viewState.dataModeType,
    config: viewState.config,
  };

  // Build viewData based on tabType, selectedMode (VIEWS), and subPage (MANAGE)
  if (tabType === 'VIEWS') {
    const currentView = viewState.view[selectedMode];
    if (!currentView) {
      throw new Error(`View data not found for mode ${selectedMode}`);
    }

    // Type-safe access based on selectedMode
    if (selectedMode === ViewType.ANALYZE) {
      const analyzeView = currentView as TabbedView[ViewType.ANALYZE];
      viewData = {
        ...viewData,
        type: selectedMode,
        selectedFolders: analyzeView.selectedFolders || [],
        showLimits: analyzeView.showLimits,
        showLabeledEvents: analyzeView.showLabeledEvents,
        pinnedTags: analyzeView.pinnedTags || [],
        hiddenTags: analyzeView.hiddenTags || [],
        selectedTags: analyzeView.selectedTags || [],
        isTrendSearch: analyzeView.isTrendSearch || false,
        trendSearchData: analyzeView.trendSearchData,
      };
    } else if (selectedMode === ViewType.INVESTIGATE) {
      const investigateView = currentView as TabbedView[ViewType.INVESTIGATE];
      viewData = {
        ...viewData,
        type: selectedMode,
        selectedFolders: investigateView.selectedFolders || [],
        showLimits: investigateView.showLimits,
        selectedTags: investigateView.selectedTags || {},
        showAnomalies: investigateView.showAnomalies ?? true,
        chartMode: investigateView.organize || 'overlays',
        severityFilter: investigateView.severityFilter,
        groupByUom: investigateView.groupByUom ?? false,
      };
    } else if (selectedMode === ViewType.MONITOR) {
      const monitorView = currentView as TabbedView[ViewType.MONITOR];
      viewData = {
        ...viewData,
        type: selectedMode,
        selectedFolders: monitorView.selectedFolders || [],
        showLimits: monitorView.showLimits,
        selectedTags: monitorView.selectedTags || [],
        severityFilter: monitorView.severityFilter || 1,
      };
    } else {
      // Fallback for any other VIEWS type
      viewData = {
        ...viewData,
        type: selectedMode,
        selectedFolders: [],
      };
    }
  } else if (tabType === 'ALERT_BOARD') {
    const alertBoardView = viewState.view[ViewType.ALERT_BOARD] || {};
    viewData = {
      ...viewData,
      type: ViewType.ALERT_BOARD,
      selected_board: alertBoardView.selected_board || '',
      selected_view: alertBoardView.selected_view || 'grid',
      filters: alertBoardView.filters || {},
    };
  } else if (tabType === 'ALERT_MANAGER') {
    const alertManagerView = viewState.view[ViewType.ALERT_MANAGER] || {};
    viewData = {
      ...viewData,
      type: ViewType.ALERT_MANAGER,
      folder: alertManagerView.folder,
      tag: alertManagerView.tag || '',
      rule_id: alertManagerView.rule_id || '',
    };
  } else if (tabType === 'LIMIT_MANAGER') {
    const limitManagerView = viewState.view[ViewType.LIMIT_MANAGER] || {};
    viewData = {
      ...viewData,
      type: ViewType.LIMIT_MANAGER,
      folder: limitManagerView.folder,
      tag: limitManagerView.tag || '',
      limit_id: limitManagerView.limit_id || '',
    };
  } else if (tabType === 'MANAGE') {
    switch (subPage) {
      case 'monitors': {
        const manageView = viewState.view[ViewType.MANAGE_MONITOR] || ({} as ManageMonitorView);
        viewData = {
          ...viewData,
          type: ViewType.MANAGE_MONITOR,
          folder: manageView.folder,
          tag: manageView.tag || '',
          ensemble_id: manageView.ensemble_id || '',
          // tabPath is frontend-only, included here for completeness
          // Backend might ignore it for now, but we preserve it in ViewState
          tabPath: manageView.tabPath,
        };
        break;
      }
      case 'formulas': {
        const manageView = viewState.view[ViewType.MANAGE_FORMULAS] || ({} as ManageFormulasView);
        viewData = {
          ...viewData,
          type: ViewType.MANAGE_FORMULAS,
          folder: manageView.folder,
          tag: manageView.tag || '',
          ensemble_id: manageView.ensemble_id || '',
        };
        break;
      }
      case 'inheritance': {
        const manageView =
          viewState.view[ViewType.MANAGE_INHERITANCE] || ({} as ManageInheritanceView);
        viewData = {
          ...viewData,
          type: ViewType.MANAGE_INHERITANCE,
          folder: manageView.folder,
          tag: manageView.tag || '',
          ensemble_id: manageView.ensemble_id || '',
        };
        break;
      }
      case 'alerts': {
        const manageView = viewState.view[ViewType.MANAGE_ALERTS] || ({} as ManageAlertsView);
        viewData = {
          ...viewData,
          type: ViewType.MANAGE_ALERTS,
          folder: manageView.folder,
          tag: manageView.tag || '',
        };
        break;
      }
      case 'user_groups': {
        const manageView =
          viewState.view[ViewType.MANAGE_USER_GROUPS] || ({} as ManageUserGroupsView);
        viewData = {
          ...viewData,
          type: ViewType.MANAGE_USER_GROUPS,
          user_group_id: manageView.user_group_id,
        };
        break;
      }
      case 'webhooks': {
        const manageView = viewState.view[ViewType.MANAGE_WEBHOOKS] || ({} as ManageWebhooksView);
        viewData = {
          ...viewData,
          type: ViewType.MANAGE_WEBHOOKS,
          webhook_id: manageView.webhook_id || '',
        };
        break;
      }
      default: {
        // Default to empty data if subPage is not recognized
        const manageView = viewState.view[ViewType.MANAGE_MONITOR] || ({} as ManageMonitorView);
        viewData = {
          ...viewData,
          type: ViewType.MANAGE_MONITOR,
          folder: manageView.folder,
          tag: manageView.tag || '',
          ensemble_id: manageView.ensemble_id || '',
          tabPath: manageView.tabPath,
        };
      }
    }
  }

  // Ensure required View properties are present before returning
  const finalViewData: View = {
    type: viewData.type || ViewType.MONITOR,
    timeSelection: viewData.timeSelection || persistTimeSelection,
    selectedFolders: viewData.selectedFolders || [],
    name: viewData.name || viewName,
    ...viewData,
  } as View;

  return {
    view: finalViewData,
    view_name: viewName,
    description: viewDescription,
    default_view: !!viewState.isDefault,
    user_id: currentUserId,
    shared,
    share_type: shareType,
    view_type: tabType,
    sub_page: subPage,
  };
};
